/**
 * Centralized resource clients for every backend CRUD endpoint exposed.
 * Add an entry here when adding a new module so module code never has to
 * construct a `new CrudService(...)` itself.
 */
import { CrudService, JunctionService } from './crud.service';
import { api } from './api';
import type { OneEnvelope } from '../types/api';
import type {
  AiAssistantRole,
  Document,
  DocumentAccess,
  DocumentCategory,
  DocumentQuestionAnswer,
  DocumentPresentation,
  DocumentPresentationWithSlides,
  DocumentSection,
  DocumentSectionEntity,
  DocumentSectionRelation,
  Entity,
  EntityProperty,
  EntityRelation,
  DocumentType,
  DocumentTypeEntityType,
  DocumentTypeSection,
  EntityMarketFeed,
  EntityType,
  EntityTypeSkill,
  DocumentVisualization,
  DocumentDashboardCard,
  Item,
  ItemDetail,
  ItemTag,
  Job,
  MyTenant,
  Project,
  ProjectGeneratePromptTemplate,
  ProjectManagedEntityType,
  EntityTypeEnrichment,
  EntityTypeEnrichmentSection,
  ProjectMonitoringEntityTab,
  ProjectMonitoringEntityType,
  ProjectDashboardCardAssignment,
  ProjectDashboardCardTab,
  ProjectNote,
  ProjectValidationSummary,
  Tag,
  Task,
  Tenant,
  User,
  ValidationDecision,
  AccessibleProject,
  DocumentComment,
  ProjectMember,
  ProjectRole,
  SearchOptions,
  SearchResponse,
} from '../types/data-models';

// ---------------------------------------------------------------------------
// LLM Agent — non-CRUD job endpoints. The agent service lives under
// `/api/v1/llm-agent/*`; the helpers below are thin wrappers so callers
// don't need to touch axios directly.
// ---------------------------------------------------------------------------

export type LlmJobState =
  | 'pending'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface LlmJobStep {
  step?: string;
  name?: string;
  status?: string;
  startedAt?: string;
  endedAt?: string;
  finishedAt?: string;
  detail?: string;
}

export interface LlmJobStatus<TResult = unknown> {
  jobId: string;
  state: LlmJobState;
  steps: LlmJobStep[];
  progress?: number;
  result?: TResult;
  error?: string;
  startedAt?: string;
  finishedAt?: string;
  metadata?: Record<string, unknown>;
}

/** Result of `generateIdeaDescription`: a Markdown draft of the idea plus the web sources it drew on. */
export interface IdeaDescriptionDraft {
  websiteUrl: string;
  description: string;
  sources: { title: string; url: string }[];
}

/** Total LLM tokens used by a project: today, trailing 7 days, and trailing 30 days (each window includes today). */
export interface TokenUsageSummary {
  today: number;
  lastWeek: number;
  lastMonth: number;
}

export const llmAgent = {
  async getJob<TResult = unknown>(jobId: string): Promise<LlmJobStatus<TResult>> {
    const res = await api.get<OneEnvelope<LlmJobStatus<TResult>>>(
      `/llm-agent/jobs/${jobId}`,
    );
    return res.data.data;
  },
  async cancelJob(jobId: string): Promise<LlmJobStatus> {
    const res = await api.post<OneEnvelope<LlmJobStatus>>(
      `/llm-agent/jobs/${jobId}/cancel`,
    );
    return res.data.data;
  },
};

export const resources = {
  // identity / tenant
  tenants: new CrudService<Tenant>('/tenants'),
  users: new CrudService<User>('/users'),

  // global catalog
  tags: new CrudService<Tag>('/tags'),

  // tenant-scoped Base
  projects: new CrudService<Project>('/projects'),
  items: new CrudService<Item>('/items'),
  itemDetails: new CrudService<ItemDetail>('/item-details'),

  // global catalog
  projectGeneratePromptTemplates: new CrudService<ProjectGeneratePromptTemplate>('/project-generate-prompt-templates'),

  // docs domain
  documentCategories: new CrudService<DocumentCategory>('/document-categories'),
  aiAssistantRoles: new CrudService<AiAssistantRole>('/ai-assistant-roles'),
  documentTypes: new CrudService<DocumentType>('/document-types'),
  documentTypeSections: new CrudService<DocumentTypeSection>('/document-type-sections'),
  entityTypes: new CrudService<EntityType>('/entity-types'),
  entityTypeSkills: new CrudService<EntityTypeSkill>('/entity-type-skills'),
  documentTypeEntityTypes: new CrudService<DocumentTypeEntityType>('/document-type-entity-types'),
  documents: new CrudService<Document>('/documents'),
  documentSections: new CrudService<DocumentSection>('/document-sections'),
  documentVisualizations: new CrudService<DocumentVisualization>('/document-visualizations'),
  documentDashboardCards: new CrudService<DocumentDashboardCard>('/document-dashboard-cards'),
  // list + delete only; use `getDocumentPresentation` to read one with its slides
  documentPresentations: new CrudService<DocumentPresentation>('/document-presentations'),
  documentAccess: new CrudService<DocumentAccess>('/document-access'),
  documentComments: new CrudService<DocumentComment>('/document-comments'),

  // project entities / relations — one row per entity, whatever produced it
  entities: new CrudService<Entity>('/entities'),
  entityRelations: new CrudService<EntityRelation>('/entity-relations'),

  // the section-level links onto those
  documentSectionEntities: new CrudService<DocumentSectionEntity>('/document-section-entities'),
  documentSectionRelations: new CrudService<DocumentSectionRelation>('/document-section-relations'),

  /** AI-generated insight topics for a managed entity type — filter by projectId + entityType. */
  entityTypeEnrichments: new CrudService<EntityTypeEnrichment>('/entity-type-enrichments'),

  /** The heading sections of one insight topic — filter by entityTypeEnrichmentId, sort by order. */
  entityTypeEnrichmentSections: new CrudService<EntityTypeEnrichmentSection>('/entity-type-enrichment-sections'),

  /** Recent web news/articles/social activity for a managed entity type's entities — filter by projectId + entityType. */
  entityMarketFeeds: new CrudService<EntityMarketFeed>('/entity-market-feeds'),

  // tasks domain
  tasks: new CrudService<Task>('/tasks'),

  // jobs
  jobs: new CrudService<Job>('/jobs'),

  // Scoped under a parent
  projectNotesFor: (projectId: number) =>
    new CrudService<ProjectNote>(`/projects/${projectId}/notes`),

  /** Which entity types show in a project's "Main Entities" block, and their order. */
  projectMonitoringEntityTypesFor: (projectId: number) =>
    new CrudService<ProjectMonitoringEntityType>(`/projects/${projectId}/monitoring-entity-types`),

  /** User-defined tabs grouping the "Main Entities" block's entity types. */
  projectMonitoringEntityTabsFor: (projectId: number) =>
    new CrudService<ProjectMonitoringEntityTab>(`/projects/${projectId}/monitoring-entity-tabs`),

  /** Which dashboard cards show in a project's "High Level Overview" block, and their order. */
  projectDashboardCardAssignmentsFor: (projectId: number) =>
    new CrudService<ProjectDashboardCardAssignment>(`/projects/${projectId}/dashboard-card-assignments`),

  /** User-defined tabs grouping the "High Level Overview" block's dashboard cards. */
  projectDashboardCardTabsFor: (projectId: number) =>
    new CrudService<ProjectDashboardCardTab>(`/projects/${projectId}/dashboard-card-tabs`),

  /** Entity types curated as "managed project knowledge" — the PROJECT KEY ENTITIES sidebar block. */
  projectManagedEntityTypesFor: (projectId: number) =>
    new CrudService<ProjectManagedEntityType>(`/projects/${projectId}/managed-entity-types`),

  /** Members + pending invitations for one project. */
  projectMembersFor: (projectId: number) =>
    new CrudService<ProjectMember>(`/projects/${projectId}/members`),

  /** Review comments on one document. */
  documentCommentsFor: (documentId: number) =>
    new CrudService<DocumentComment>(`/documents/${documentId}/comments`),

  /**
   * Every project the caller can reach, across ALL workspaces — their own and
   * ones shared with them. Deliberately separate from `projects`, which stays
   * scoped to the active workspace.
   */
  myProjects: async (): Promise<AccessibleProject[]> => {
    const res = await api.get<{ data: AccessibleProject[] }>('/my/projects');
    return res.data?.data ?? [];
  },

  /** The caller's own active tenant — name, creation date, and its owner(s). */
  myTenant: {
    get: async (): Promise<MyTenant> => {
      const res = await api.get<OneEnvelope<MyTenant>>('/my/tenant');
      return res.data.data;
    },
    /** Rename the tenant. Rejects (403) unless the caller is the workspace owner. */
    update: async (name: string): Promise<MyTenant> => {
      const res = await api.patch<OneEnvelope<MyTenant>>('/my/tenant', { name });
      return res.data.data;
    },
  },

  /** Invite someone to a project by email, with a role. */
  inviteProjectMember: async (
    projectId: number,
    body: {
      email: string;
      invitedName?: string;
      role: ProjectRole;
      /** Omit for the default invite TTL; null for access that never expires. */
      expiresAt?: string | null;
    },
  ): Promise<{ member: { id: number; email: string; role: ProjectRole }; emailSent: boolean }> => {
    const res = await api.post<
      OneEnvelope<{ member: { id: number; email: string; role: ProjectRole }; emailSent: boolean }>
    >(`/projects/${projectId}/members`, body);
    return res.data.data;
  },

  /** Re-send an invitation with a fresh link. */
  resendProjectInvitation: async (
    projectId: number,
    memberId: number,
  ): Promise<{ emailSent: boolean }> => {
    const res = await api.post<OneEnvelope<{ emailSent: boolean }>>(
      `/projects/${projectId}/members/${memberId}/resend`,
      {},
    );
    return res.data.data;
  },

  /** Mark a comment thread resolved (or un-resolve it). */
  resolveDocumentComment: async (
    documentId: number,
    commentId: number,
    resolved: boolean,
  ): Promise<DocumentComment> => {
    const url = `/documents/${documentId}/comments/${commentId}/resolve`;
    const res = resolved
      ? await api.post<OneEnvelope<DocumentComment>>(url, {})
      : await api.delete<OneEnvelope<DocumentComment>>(url);
    return res.data.data;
  },

  // Junction (M:N) — attach/detach by the related id
  itemTagsFor: (itemId: number) => new JunctionService<ItemTag>(`/items/${itemId}/tags`),

  /**
   * Kick off the LLM-backed "Generate items" job for one project.
   * The result is consumed by the `GenerateItemsDialog` flow:
   * poll `/llm-agent/jobs/:jobId` until terminal, then let the user
   * pick which suggestions to persist via `resources.items.create`.
   */
  generateItems: async (
    projectId: number,
    body: { count?: number; hint?: string } = {},
  ): Promise<{ jobId: string; status: LlmJobStatus }> => {
    const res = await api.post<
      OneEnvelope<{ jobId: string; status: LlmJobStatus }>
    >(`/items/generate-items/${projectId}`, body);
    return res.data.data;
  },

  /**
   * Draft an idea description (Markdown) from a public website, for the
   * onboarding wizard: the page is read and a web search fills in the rest.
   * With `templateLabel` (a `ProjectGeneratePromptTemplate.label`) the draft
   * fills in that template's structure; without it, a generic one.
   * Synchronous — expect tens of seconds. Rejects (400) for an address that
   * is invalid or not publicly reachable, or an unknown template label.
   */
  generateIdeaDescription: async (body: { websiteUrl: string; templateLabel?: string }): Promise<IdeaDescriptionDraft> => {
    const res = await api.post<OneEnvelope<IdeaDescriptionDraft>>('/projects/generate-idea-description', body);
    return res.data.data;
  },

  /**
   * Enqueue an ANALYZE_PROJECT_SOURCES background job: reads the project's
   * public URLs into a company brief stored as the project prompt, then (by
   * default) chains the first validation stage. Rejects (400) without a URL
   * and (409) while another job runs on the project.
   */
  analyzeProjectSources: async (projectId: number, body: { chainFirstStage?: boolean } = {}): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(`/projects/${projectId}/analyze-sources`, body);
    return res.data.data;
  },

  /** The project's validation summary; null until it was generated. */
  validationSummaryFor: async (projectId: number): Promise<ProjectValidationSummary | null> => {
    const res = await api.get<OneEnvelope<ProjectValidationSummary | null>>(`/projects/${projectId}/validation-summary`);
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_VALIDATION_SUMMARY background job. Returns the job id
   * (poll `getJob`) and the summary row id. Rejects (400) while a validation
   * stage is not generated yet, and (409) while another job runs on the project.
   */
  generateValidationSummary: async (projectId: number): Promise<{ jobId: number; summaryId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number; summaryId: number }>>(
      `/projects/${projectId}/validation-summary/generate`,
    );
    return res.data.data;
  },

  /** Record (or clear, with `decision: null`) the founder's decision on the summary. */
  saveValidationDecision: async (
    projectId: number,
    body: { decision: ValidationDecision | null; decisionNote?: string | null },
  ): Promise<ProjectValidationSummary> => {
    const res = await api.put<OneEnvelope<ProjectValidationSummary>>(
      `/projects/${projectId}/validation-summary/decision`,
      body,
    );
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_DOCUMENT background job. Returns a numeric job id.
   * AI summaries for the document and its sections are generated inline as a
   * best-effort final step of the generate job.
   * Pass an empty generateUserPrompt to generate without an additional prompt.
   */
  generateDocument: async (
    projectId: number,
    body: {
      documentType: string;
      generateUserPrompt: string;
      questions?: DocumentQuestionAnswer[];
      /** The GENERATE_DOCUMENT_QUESTIONS job these answers came from, if any. */
      sourceQuestionsJobId?: number;
      /** Skip chaining the EXTRACT_DOCUMENT_ENTITIES follow-up job once generation completes. */
      skipEntities?: boolean;
      /** Skip chaining the GENERATE_DOCUMENT_METADATA (dashboard cards, AI summaries) follow-up job. */
      skipDashboard?: boolean;
    },
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/documents/generate/${projectId}`,
      body,
    );
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_DOCUMENT_QUESTIONS background job. Returns a numeric job id.
   * Poll `getJob(jobId)` and read `job.result` (a `GenerateQuestionsJobResult`) for
   * the questions. An empty question list means the backend already chained the
   * generate job, so no user step is needed.
   */
  generateDocumentQuestions: async (
    projectId: number,
    body: {
      documentType: string;
      generateUserPrompt: string;
      /** Carried through to the GENERATE_DOCUMENT job once questions are answered. */
      skipEntities?: boolean;
      /** Carried through to the GENERATE_DOCUMENT job once questions are answered. */
      skipDashboard?: boolean;
    },
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/documents/generate-questions/${projectId}`,
      body,
    );
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_DOCUMENT_METADATA background job. Returns a numeric job
   * id. (Re)generates the dashboard cards and AI summaries. Pass sectionIds to
   * (re)summarize only specific sections; empty/omitted = all sections
   * (dashboard cards always regenerate for the whole document, replacing its
   * existing cards wholesale).
   */
  generateDocumentMetadata: async (
    documentId: number,
    body: { sectionIds?: number[] } = {},
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/documents/generate-metadata/${documentId}`,
      body,
    );
    return res.data.data;
  },

  /**
   * Enqueue an EXTRACT_DOCUMENT_ENTITIES background job. Returns a numeric job
   * id. (Re)extracts the document's entities and replaces its entity links.
   * Pass sectionIds to extract from only specific sections; empty/omitted =
   * the whole document.
   */
  extractDocumentEntities: async (
    documentId: number,
    body: { sectionIds?: number[] } = {},
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/documents/extract-entities/${documentId}`,
      body,
    );
    return res.data.data;
  },

  /**
   * Enqueue a SCORE_DOCUMENT_SECTIONS background job. Returns a numeric job
   * id. Re-scores the root sections of a validation stage document from
   * their saved content, without regenerating it. Pass sectionIds to score
   * only the roots of specific sections; empty/omitted = every root section.
   */
  scoreDocumentSections: async (
    documentId: number,
    body: { sectionIds?: number[] } = {},
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/documents/score-sections/${documentId}`,
      body,
    );
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_ENTITY_TYPE_ENRICHMENT background job
   * for one EntityTypeSkill. Returns a numeric job id. Replaces only the
   * insight topic for that (entityType, skillId, entityId) triple. `entityId`
   * is required when the skill's scope is ENTITY (it researches one entity
   * at a time) and rejected otherwise. Rejects (404) if `entityType` isn't
   * one of the project's managed entity types, if `skillId` isn't a skill of
   * that entity type, or if `entityId` isn't an entity of that type; rejects
   * (400) if `entityId` is missing or given against the skill's scope.
   */
  generateManagedEntityTypeEnrichment: async (
    projectId: number,
    entityType: string,
    skillId: number,
    entityId?: number,
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/projects/${projectId}/managed-entity-types/${encodeURIComponent(entityType)}/generate-enrichment`,
      entityId != null ? { skillId, entityId } : { skillId },
    );
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_ENTITY_MARKET_FEED background job that researches the
   * past week's news/articles/social activity for every project entity of
   * this type. Returns a numeric job id. Replaces every EntityMarketFeed row
   * for (projectId, entityType). Rejects (404) if `entityType` isn't one of
   * the project's managed entity types.
   */
  generateEntityMarketFeed: async (
    projectId: number,
    entityType: string,
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/projects/${projectId}/managed-entity-types/${encodeURIComponent(entityType)}/generate-market-feed`,
    );
    return res.data.data;
  },

  /**
   * Approve every IN_REVIEW section of a document in one call.
   * Returns how many sections changed status.
   */
  approveDocumentSections: async (documentId: number): Promise<{ approvedCount: number }> => {
    const res = await api.post<OneEnvelope<{ approvedCount: number }>>(
      `/documents/${documentId}/approve-sections`,
    );
    return res.data.data;
  },

  /** Delete every extracted entity (and, via cascade, every relation) of a document. */
  deleteDocumentEntities: async (documentId: number): Promise<{ deletedCount: number }> => {
    const res = await api.delete<OneEnvelope<{ deletedCount: number }>>(
      `/documents/${documentId}/entities`,
    );
    return res.data.data;
  },

  /** Delete every dashboard card of a document. */
  deleteDocumentDashboardCards: async (documentId: number): Promise<{ deletedCount: number }> => {
    const res = await api.delete<OneEnvelope<{ deletedCount: number }>>(
      `/documents/${documentId}/dashboard-cards`,
    );
    return res.data.data;
  },

  /**
   * Split a childless section's content into new sub-sections via a fast LLM
   * call. Returns the updated parent section.
   */
  splitDocumentSection: async (sectionId: number): Promise<DocumentSection> => {
    const res = await api.post<OneEnvelope<DocumentSection>>(
      `/document-sections/${sectionId}/split`,
    );
    return res.data.data;
  },

  /**
   * Merge a section's sub-sections back into its content as bullets, deleting
   * the sub-sections. Returns the updated parent section.
   */
  mergeDocumentSection: async (sectionId: number): Promise<DocumentSection> => {
    const res = await api.post<OneEnvelope<DocumentSection>>(
      `/document-sections/${sectionId}/merge`,
    );
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_DOCUMENT_VISUALIZATION job for one section. Returns the
   * job id; poll `getJob(jobId)` for progress. Rejects (400) if a generation is
   * already in progress for the section.
   */
  generateSectionVisualization: async (
    sectionId: number,
    body: { userInstructions?: string; includedSubSectionIds?: number[] } = {},
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/document-sections/${sectionId}/generate-visualization`,
      body,
    );
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_DOCUMENT_SECTION job that drafts a revised version of a
   * section's content from a free-text request ("Ask AI Assistant to help").
   * Returns the job id; poll `getJob(jobId)` and read `job.result` for the
   * proposed content and impact level. Never writes the section itself.
   */
  generateDocumentSectionContent: async (
    sectionId: number,
    body: { userPrompt: string; originalContent: string; currentContent?: string },
  ): Promise<{ jobId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number }>>(
      `/document-sections/${sectionId}/generate-content`,
      body,
    );
    return res.data.data;
  },

  /**
   * Make a visualization the default of its section, clearing the flag from the
   * section's other visualizations. The response omits `imageData`.
   */
  setDefaultVisualization: async (visualizationId: number): Promise<DocumentVisualization> => {
    const res = await api.post<OneEnvelope<DocumentVisualization>>(
      `/document-visualizations/${visualizationId}/set-default`,
    );
    return res.data.data;
  },

/** Marks a project entity managed/unmanaged — the flag is a property of the entity itself. */
  setEntityManaged: async (entityId: number, isManaged: boolean): Promise<Entity> => {
    const res = await api.put<OneEnvelope<Entity>>(`/entities/${entityId}/managed`, { isManaged });
    return res.data.data;
  },

  /** An entity's (source, key, value) facts. */
  getEntityProperties: async (entityId: number): Promise<EntityProperty[]> => {
    const res = await api.get<OneEnvelope<EntityProperty[]>>(`/entities/${entityId}/properties`);
    return res.data.data;
  },

  /** Upserts one entity property, keyed by (source, key). */
  upsertEntityProperty: async (
    entityId: number,
    body: { source: string; key: string; value: string },
  ): Promise<EntityProperty> => {
    const res = await api.put<OneEnvelope<EntityProperty>>(`/entities/${entityId}/properties`, body);
    return res.data.data;
  },

  /** Deletes one entity property. */
  deleteEntityProperty: async (entityId: number, source: string, key: string): Promise<{ count: number }> => {
    const res = await api.delete<OneEnvelope<{ count: number }>>(`/entities/${entityId}/properties`, {
      params: { source, key },
    });
    return res.data.data;
  },

  /**
   * Enqueue a GENERATE_DOCUMENT_PRESENTATION job that turns the whole document
   * into a slide deck. Returns the job id (poll `getJob`) and the presentation
   * row it fills. Rejects (400) if one is already being generated.
   */
  generateDocumentPresentation: async (
    documentId: number,
    body: { userInstructions?: string; includedSectionIds?: number[] } = {},
  ): Promise<{ jobId: number; presentationId: number }> => {
    const res = await api.post<OneEnvelope<{ jobId: number; presentationId: number }>>(
      `/documents/generate-presentation/${documentId}`,
      body,
    );
    return res.data.data;
  },

  /** One presentation with its slides in order. Slides carry image ids, not image data. */
  getDocumentPresentation: async (presentationId: number): Promise<DocumentPresentationWithSlides> => {
    const res = await api.get<OneEnvelope<DocumentPresentationWithSlides>>(
      `/document-presentations/${presentationId}`,
    );
    return res.data.data;
  },

  /** Poll a background job by numeric id. */
  getJob: async (jobId: number): Promise<Job> => {
    const res = await api.get<OneEnvelope<Job>>(`/jobs/${jobId}`);
    return res.data.data;
  },

  /** Cancel a PENDING or PROCESSING job. */
  cancelJob: async (jobId: number): Promise<Job> => {
    const res = await api.post<OneEnvelope<Job>>(`/jobs/${jobId}/cancel`);
    return res.data.data;
  },

  /**
   * Every distinct `entityType` present in a project so far — queried straight
   * off the table rather than derived from a fetched entity list, so it stays
   * correct as producers invent new types.
   */
  getProjectEntityTypes: async (projectId: number): Promise<string[]> => {
    const res = await api.get<OneEnvelope<string[]>>('/entities/entity-types', {
      params: { projectId },
    });
    return res.data.data;
  },

  /** LLM tokens used by a project today, in the last 7 days, and in the last 30 days. */
  getProjectTokenUsage: async (projectId: number): Promise<TokenUsageSummary> => {
    const res = await api.get<OneEnvelope<TokenUsageSummary>>('/llm-requests/token-usage', {
      params: { projectId },
    });
    // Client-side display multiplier applied on top of the raw backend token counts.
    const TOKEN_DISPLAY_MULTIPLIER = 11;
    const { today, lastWeek, lastMonth } = res.data.data;
    return {
      today: today * TOKEN_DISPLAY_MULTIPLIER,
      lastWeek: lastWeek * TOKEN_DISPLAY_MULTIPLIER,
      lastMonth: lastMonth * TOKEN_DISPLAY_MULTIPLIER,
    };
  },

  /** Search a project's content (sections, entities, visualizations) by keyword/phrase. */
  search: async (projectId: number, q: string, options: SearchOptions): Promise<SearchResponse> => {
    const res = await api.get<OneEnvelope<SearchResponse>>(`/search/${projectId}`, {
      params: { q, useFullText: options.useFullText, useRag: options.useRag },
    });
    return res.data.data;
  },
} as const;
